import useMapStore from "@/store/mapStore";
import { useState, useEffect } from "react";

interface ParkAssistResult {
  id: number;
  visit: {
    id: number;
    plate: {
      text: string;
    };
  };
}

export interface ParkAssistImage {
  visitId: number;
  bayId: number;
  imageUrl: string;
}

export enum FetchStatus {
  Loading = "loading",
  Loaded = "loaded",
  Error = "error",
  TooManyRequests = "tooManyRequests",
}

export default function useParkAssist({ plateText }: { plateText: string }) {
  const [imageData, setImageData] = useState<ParkAssistImage[]>([]);
  const [fetchStatus, setFetchStatus] = useState<FetchStatus>(
    FetchStatus.Loading,
  );

  const appSession = useMapStore((state) => state.appSession);

  useEffect(() => {
    const fetchImageData = async () => {
      try {
        // Set loading state
        setFetchStatus(FetchStatus.Loading);

        // Fetch results from the first API endpoint
        const resultsResponse = await fetch(
          `https://api.proximi.fi/park-assist-v2?query=${plateText}&session_id=${appSession}`,
          {
            headers: {
              Authorization: `Bearer ${import.meta.env.VITE_PROXIMIIO_TOKEN}`,
            },
          },
        );

        if (resultsResponse.status === 429) {
          setFetchStatus(FetchStatus.TooManyRequests);
          return;
        }

        if (!resultsResponse.ok) {
          throw new Error(
            `Request failed with status ${resultsResponse.status}`,
          );
        }

        const results: ParkAssistResult[] = await resultsResponse.json();

        // Fetch images for each visit ID
        //@ts-ignore
        const imagePromises = results.map(async (result) => {
          const imageResponse = await fetch(
            `https://api.proximi.fi/park-assist-v2/visits/${result.visit.id}?&session_id=${appSession}`,
            {
              headers: {
                Authorization: `Bearer ${import.meta.env.VITE_PROXIMIIO_TOKEN}`,
              },
            },
          );

          if (imageResponse.status === 429) {
            setFetchStatus(FetchStatus.TooManyRequests);
            return null; // skip this image
          }

          if (!imageResponse.ok) {
            throw new Error(
              `Image fetch failed with status ${imageResponse.status}`,
            );
          }

          const blob = await imageResponse.blob();
          const imageUrl = URL.createObjectURL(blob);
          return { visitId: result.visit.id, bayId: result.id, imageUrl };
        });

        const imageData = await Promise.all(imagePromises);
        //@ts-ignore
        setImageData(imageData);
        setFetchStatus(FetchStatus.Loaded);
      } catch (error) {
        console.error("Error fetching image data:", error);
        // Set error state
        setFetchStatus(FetchStatus.Error);
      }
    };

    if (plateText) {
      fetchImageData();
    }
  }, [plateText, appSession]);

  return [imageData, fetchStatus];
}
